import React, { useEffect, useState } from "react";
import { Col, DatePicker, Form, Row } from "antd";
import Button from "atoms/Button";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { connect, useSelector } from "react-redux";
import {
    getAllDepartmentList,
    getManagingOffice,
    getUserInfo,
    getUserList,
} from "redux/selectors";
import { concat, every, flatMap, map, some, uniqBy } from "lodash";
import Table from "atoms/Table";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import moment from "moment";
import { Breadcrumbs, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { currencyAmount } from "utils/functions";
import { Grade } from "common/Constants";
import { findWeekIdsInRange } from "utils/Date";
import { useFetch } from "hooks/useFetch";
import queryKeys from "common/queryKey";
import AddResourceModal from "./Addresourcemodal";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const AvailableColor = "#EDFFF7";
const BookedColor = "#FFD3D3";

// FIX: Converts Moment/dayjs/Date objects to ISO strings so history.pushState
// doesn't throw DataCloneError (it requires structured-cloneable plain values).
const serializeState = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (moment.isMoment(obj)) return obj.toISOString();
    if (dayjs.isDayjs(obj)) return obj.toISOString();
    if (obj instanceof Date) return obj.toISOString();
    if (typeof obj === "function") return undefined;
    if (Array.isArray(obj)) return obj.map(serializeState).filter((v) => v !== undefined);
    if (typeof obj === "object") {
        return Object.fromEntries(
            Object.entries(obj)
                .map(([k, v]) => [k, serializeState(v)])
                .filter(([, v]) => v !== undefined)
        );
    }
    return obj;
};

const isDateInBookingRange = (dayMoment, entry) => {
    if (!dayMoment) return false;
    // Accept both moment objects and plain strings/dates
    const day = moment.isMoment(dayMoment) ? dayMoment : moment(dayMoment);
    if (!day.isValid()) return false;

    const start = moment(entry?.actualBookedStartDate);
    const end = moment(entry?.actualBookedEndDate).add(1, "day");
    if (!start.isValid() || !end.isValid()) return false;

    return day.isBetween(start, end, undefined, "[)");
};

// ─── Schedule cell component ──────────────────────────────────────────────────
const Schedule = ({ schedule, bookedUserList, isUserBooked }) => {
    const [renderIndex, setRenderIndex] = useState(0);
    const itemsPerPage = 7;

    // Guard: if schedule is empty or invalid, render nothing
    if (!schedule || schedule.length === 0) return <ScheduleWrap>—</ScheduleWrap>;

    return (
        <ScheduleWrap>
            <Button
                className="schedule rounded-3 back"
                type="primary"
                onClick={() => setRenderIndex((i) => Math.max(0, i - itemsPerPage))}
                disabled={renderIndex === 0}
            >
                <KeyboardArrowLeftIcon />
            </Button>
            {schedule.slice(renderIndex, renderIndex + itemsPerPage).map((day) => {
                if (!day || !day.fullDate) return null; // ← safety net
                const isBetween = Array.isArray(bookedUserList) && bookedUserList.some((entry) =>
                    isDateInBookingRange(day.fullDate, entry)
                );
                return (
                    <div
                        key={day.date}
                        className={isUserBooked && isBetween ? "day booked" : "day available"}
                    >
                        {day.date}
                    </div>
                );
            })}
            <Button
                className="schedule rounded-3 next"
                type="primary"
                onClick={() => setRenderIndex((i) => i + itemsPerPage)}
                disabled={renderIndex + itemsPerPage >= schedule.length}
            >
                <KeyboardArrowRightIcon />
            </Button>
        </ScheduleWrap>
    );
};
const Legend = ({ available = false }) => (
    <div className="d-flex gap-2 align-items-center">
        <div className={available ? "legend-box available" : "legend-box booked"} />
        <span>{available ? "Available" : "Booked"}</span>
    </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AddResourcesPage = ({ userList = [], allDepartmentList = [] }) => {
    const history = useHistory();
    const location = useLocation();
    const {
        resourceList: existingResourceList = [],
        formTitle = "Creating Matter",
        // planStartDate = null,    // ADD THIS
        // planEndDate = null,      // ADD THIS
    } = location.state || {};

    const excludedUserIds = new Set(
        [location.state?.leadAttorneyId, location.state?.referringAttorneyId]
            .filter((id) => id !== undefined && id !== null && id !== "")
    );

    const rawStart = location.state?.planStartDate;
    const rawEnd = location.state?.planEndDate;

    // ✅ convert back to dayjs
    const planStartDate = rawStart ? dayjs(rawStart) : null;
    const planEndDate = rawEnd ? dayjs(rawEnd) : null;

    const userInfo = useSelector(getUserInfo);
    const manageoffice = useSelector(getManagingOffice);

    const managinOfficeOptions = (manageoffice || []).map((v) => ({ label: v, value: v }));

    const filteruser = userList?.filter(
        (val) => val?.userid === userInfo?.userid && val?.statusid === "1"
    );

    const [showAddResourceModal, setShowAddResourceModal] = useState(false);
    const [selectedResource, setSelectedResource] = useState({});
    const [bookedUserList, setBookedUserList] = useState([]);
    const [allResourceList, setAllResourceList] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState([
        moment(),
        moment().add(30, "days"),
    ]);
    const [form] = Form.useForm();

    const { getAllocatedResourceList } = queryKeys;
    const weekList = findWeekIdsInRange(selectedDateRange);
    const { key, url } = getAllocatedResourceList(weekList?.join(", "));

    const { data: resourceAllocatedData, isLoading, refetch } = useFetch(
        key,
        url,
        { enabled: weekList?.length > 0 },
        {
            engagementId: "",
            weekLists: weekList,
            startDate: new Date(selectedDateRange[0]),
            endDate: new Date(selectedDateRange[1]),
        }
    );

    useEffect(() => { refetch(); }, [selectedDateRange]);

    useEffect(() => {
        getData(resourceAllocatedData?.response?.response);
    }, [resourceAllocatedData]);

    const generateSchedule = (startDate, endDate) => {
        const schedule = [];
        if (!startDate || !endDate) return schedule;
        let current = moment(startDate);
        const end = moment(endDate);
        if (!current.isValid() || !end.isValid()) return schedule;

        while (current.isSameOrBefore(end, "day")) {
            const dow = current.day();
            if (dow !== 0 && dow !== 6) {
                schedule.push({
                    date: current.format("MMM DD"),
                    available: true,
                    fullDate: current.clone(), // ← clone() is critical here
                });
            }
            current.add(1, "day");
        }
        return schedule;
    };

    const getAllowedCountries = () => {
        const loggedInUser = filteruser[0];
        if (loggedInUser?.subcluster === true && loggedInUser?.subclustercountries) {
            return loggedInUser.subclustercountries;
        }
        return [loggedInUser?.managingoffice?.[0]];
    };

    const filterUserBasedOnManagingOffice = (item) => {
        const allowedCountries = getAllowedCountries();
        const fullUser = userList?.find((u) => u.userid === (item?.userid || item?.userId || item?.id));
        const userManagingOffice = fullUser?.managingoffice;
        const userSubclusterCountries = fullUser?.subclustercountries ?? [];
        return (
            allowedCountries.some((c) => userManagingOffice?.includes(c)) ||
            allowedCountries.some((c) => userSubclusterCountries.includes(c))
        );
    };

    const getData = (response) => {
        if (!response) return;
        const [startDate, endDate] = selectedDateRange;
        const flattenedArray = concat(response?.flat(), []);
        setBookedUserList(flattenedArray);
        const uniqBookedList = uniqBy(flattenedArray, "userId");

        const alreadyAddedIds = (existingResourceList || [])
            .map((r) => r.userId)
            .filter(Boolean);

        let filteredArray;
        if (filteruser[0]?.OfficeSupervisorId === "none") {
            filteredArray = userList?.filter(
                (item1) =>
                    !flattenedArray.some((item2) => item2?.userId && item1.userid === item2.userId) &&
                    !alreadyAddedIds.includes(item1.userid) &&
                    !excludedUserIds.has(item1.userid) &&   // ← NEW: exclude attorneys
                    item1.statusid === "1"
            );
        } else {
            filteredArray = userList?.filter(
                (item1) =>
                    !flattenedArray.some((item2) => item2?.userId && item1.userid === item2.userId) &&
                    !alreadyAddedIds.includes(item1.userid) &&
                    !excludedUserIds.has(item1.userid) &&   // ← NEW: exclude attorneys
                    filterUserBasedOnManagingOffice(item1) &&
                    item1.statusid === "1"
            );
        }

        const transformedAvailable = map(filteredArray, (userData) => ({
            userId: userData?.userid,
            name: userData?.fullname,
            status: "Completely available",
            employeeId: userData?.employeeid,
            resourceExtRate: userData?.chargeoutrate,
            country: userData?.managingoffice,
            schedule: generateSchedule(startDate, endDate),
        })).filter((u) => filterUserBasedOnManagingOffice({ userId: u.userId }));

        const transformedBooked = map(uniqBookedList, (userData) => {
            // ← NEW: skip attorneys in the booked list too
            if (excludedUserIds.has(userData?.userId)) return null;

            const filtered = flattenedArray.filter((u) => u.userId === userData?.userId);
            const chargeOutRate = userList?.find((u) => u?.userid === userData?.userId)?.chargeoutrate;
            const fullUser = userList?.find((u) => u?.userid === userData?.userId && u?.statusid === "1");
            const schedule = generateSchedule(startDate, endDate);

            const isWithinRange = every(schedule, (entry) =>
                some(filtered, (e) => isDateInBookingRange(entry?.fullDate, e))
            );

            return {
                userId: userData?.userId,
                name: userData?.employeeName,
                status: isWithinRange ? "Booked" : "Partially available",
                employeeId: userData?.employeeId,
                resourceExtRate: chargeOutRate,
                country: fullUser?.managingoffice,
                schedule,
            };
        })
            .filter(Boolean)  // ← remove the nulls from excluded attorneys
            .filter((u) => filterUserBasedOnManagingOffice({ userId: u.userId }));

        setAllResourceList(concat(transformedAvailable, transformedBooked));
    };

    const gradeOptions = Grade.map((v) => ({ label: v, value: v }));

    const resourceType = [
        { label: "Completely Available", value: "Completely available" },
        { label: "Partially Available", value: "Partially available" },
        { label: "Booked", value: "Booked" },
    ];

    const dataSource = allResourceList.map((resource) => {
        const filterUserBookedList = bookedUserList.filter((u) => u.userId === resource.userId);
        const isUserBooked = some(bookedUserList, { userId: resource.userId });
        const filteredUser = userList?.filter((u) => u?.userid === resource?.userId);
        const chargeOutRate = userList?.find((u) => u?.userid === resource?.userId)?.chargeoutrate;
        const grade = filteredUser[0]?.grade;

        return {
            key: resource.userId,
            name: resource.name,
            chargeOutRate: currencyAmount("$", chargeOutRate),
            status: resource?.status,
            country: resource?.country,
            grade,
            schedule: (
                <Schedule
                    schedule={resource.schedule}
                    bookedUserList={filterUserBookedList}
                    isUserBooked={isUserBooked}
                />
            ),
            action: (
                <Button
                    className="rounded-3"
                    type="primary"
                    onClick={() => {
                        if (!resource?.userId) return;
                        setSelectedResource(resource);
                        setShowAddResourceModal(true);
                    }}
                >
                    Add
                </Button>
            ),
        };
    });

    const getFilteredData = () => {
        const userid = form.getFieldValue("userid") || [];
        const available = form.getFieldValue("available") || [];
        const country = form.getFieldValue("country") || [];
        const grade = form.getFieldValue("grade") || [];

        if (!userid.length && !available.length && !country.length && !grade.length) {
            return dataSource;
        }
        return dataSource.filter((item) => {
            const userMatch = userid.length ? userid.some((n) => item.key === n) : true;
            const countryMatch = country.length ? country.some((n) => item.country?.includes(n)) : true;
            const gradeMatch = grade.length ? grade.some((n) => String(n) === String(item.grade)) : true;
            const availableMatch = available.length ? available.includes(item.status) : true;
            return userMatch && availableMatch && countryMatch && gradeMatch;
        });
    };

    const resourceColumns = [
        { title: "Resource Name", dataIndex: "name", key: "name" },
        { title: "Charge Out / hr", dataIndex: "chargeOutRate", key: "chargeOutRate" },
        { title: "Action", dataIndex: "action", key: "action" },
        { title: "Schedule", dataIndex: "schedule", key: "schedule" },
    ];
    const leadAttorneyId = location.state?.leadAttorneyId;

    const leadAttorneyName =
        userList?.find((u) => u.userid === leadAttorneyId)?.fullname || "";

    const handleBackToMatter = () => {
        const state = location.state || {};

        if (state.fromMatterDetails) {
            const matterId =
                state?.editData?.matterId ||
                state?.matterId ||
                "";
            history.push(`/matter-details/${matterId}`);
            return;
        }

        const matterId =
            state?.editData?.matterId ||
            state?.matterId ||
            "";
        history.push(
            state.editMode ? `/create-matter/${matterId}` : "/create-matter",
            serializeState({
                openStep: 1,
                form1State: state.form1State,
                form2State: state.form2State,
                form3State: state.form3State,
                resourceList: state.resourceList || [],
                subcontractorList: state.subcontractorList || [],
                editMode: state.editMode,
                editData: state.editData,
                fromAddResource: true,
            })
        );
    };
    
    const breadcrumbs = [
        <label
            key="1"
            style={{ color: "#636980", cursor: "pointer" }}
            onClick={handleBackToMatter}
        >
            {/* {formTitle} */}Back
        </label>,
        <Typography key="2" color="text.primary">Add Resources</Typography>,
    ];

    const getPending = () => {
        try {
            return JSON.parse(sessionStorage.getItem("pendingResources") || "[]");
        } catch {
            return [];
        }
    };



    return (
        <PageWrap>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                {breadcrumbs}
            </Breadcrumbs>
            <br />

            <Row>
                <Col sm={24}>
                    <Form
                        form={form}
                        layout="vertical"
                        className="mt-2"
                        onValuesChange={() => form.submit()}
                    >
                        <Row gutter={12} className="g-3 mb-2">
                            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                                <DropdownCheckBox
                                    name="userid"
                                    label="Resource"
                                    options={uniqBy(dataSource.map((i) => ({ label: i.name, value: i.key })), "value")}
                                    form={form}
                                />
                            </Col>
                            {filteruser?.[0]?.managingoffice?.[0] === "Head Office" && (
                                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                                    <DropdownCheckBox name="country" label="Country" options={managinOfficeOptions} form={form} />
                                </Col>
                            )}
                            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                                <DropdownCheckBox name="grade" label="Grade" options={gradeOptions} form={form} />
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                                <DropdownCheckBox name="available" label="Availability" options={resourceType} form={form} />
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                                <RangePicker
                                    value={selectedDateRange}
                                    onChange={(dates) => setSelectedDateRange(dates)}
                                    format="DD-MM-YYYY"
                                    style={{ width: "300px", borderRadius: "16px" }}
                                    allowClear={false}
                                />
                            </Col>
                        </Row>
                    </Form>
                </Col>
                <div className="legend">
                    <Legend available />
                    <Legend />
                </div>
            </Row>

            <Table
                columns={resourceColumns}
                dataSource={getFilteredData()}
                rowKey="key"
                loading={isLoading}
            />

            <AddResourceModal
                open={showAddResourceModal}
                fromAddResourcePage={true}
                onClose={() => setShowAddResourceModal(false)}
                selectedResource={selectedResource}
                matterData={location.state?.editData || null}
                planStartDate={planStartDate}
                planEndDate={planEndDate}
                leadAttorneyName={leadAttorneyName}
                fetchResourceList={() => {
                    const state = location.state || {};
                    const pending = getPending();

                    const matterId =
                        state?.editData?.matterId ||
                        state?.matterId ||
                        location.pathname.split("/").pop(); // MID00079

                    const updatedResourceList = [
                        ...(state.resourceList || []),
                        ...pending,
                    ].filter(
                        (v, i, arr) =>
                            v?.userId &&
                            arr.findIndex((x) => x?.userId === v?.userId) === i
                    );

                    // IMPORTANT FIX:
                    // while editing, navigate back to actual edit route
                    // /create-matter/MID00079
                    history.push(
                        state.editMode
                            ? `/create-matter/${matterId}`
                            : "/create-matter",
                        serializeState({
                            openStep: 1,
                            form1State: state.form1State,
                            form2State: state.form2State,
                            form3State: state.form3State,

                            resourceList: updatedResourceList,
                            subcontractorList: state.subcontractorList || [],

                            editMode: state.editMode,
                            editData: state.editData,

                            fromAddResource: true,
                        })
                    );
                }}

                onAdd={(newResource) => {
                    if (!newResource?.userId) return;

                    const existing = getPending();

                    const normalized = {
                        key: newResource.userId,
                        // Preserve resourceId for existing resources; null for new ones
                        resourceId:
                            newResource.resourceId ||
                            newResource.raw?.resourceId ||
                            null,
                        userId: newResource.userId || newResource.userid || "",
                        employeeId:
                            newResource.employeeId ||
                            newResource.employeeid ||
                            "",
                        name:
                            newResource.employeeName ||
                            newResource.name ||
                            "",
                        role:
                            newResource.projectRole ||
                            newResource.role ||
                            "",
                        mandays:
                            newResource.personDay ||
                            newResource.mandays ||
                            0,
                        chargeOut: `$${newResource.resourceExtRate || 0}`,
                        startDate: newResource.startDate,
                        finishDate: newResource.finishDate,
                        status: newResource.status || "Assigned",
                        justification: newResource.justification || newResource.raw?.justification || "",
                        raw: {
            ...(newResource.raw || {}),
            justification: newResource.justification || newResource.raw?.justification || "", // ✅ ADD THIS
            resourceExtRate: newResource.resourceExtRate || newResource.chargeOutRate || 0,
            chargeOutRate: newResource.resourceExtRate || newResource.chargeOutRate || 0,
        },
                    };

                    const updated = [...existing, normalized].filter(
                        (v, i, arr) =>
                            v?.userId &&
                            arr.findIndex((x) => x?.userId === v?.userId) === i
                    );

                    sessionStorage.setItem(
                        "pendingResources",
                        JSON.stringify(updated)
                    );

                    setShowAddResourceModal(false);

                    const state = location.state || {};

                    const matterId =
                        state?.editData?.matterId ||
                        state?.matterId ||
                        location.pathname.split("/").pop();

                    // IMPORTANT FIX:
                    // edit mode should go to /create-matter/MID00079
                    history.push(
                        state.editMode
                            ? `/create-matter/${matterId}`
                            : "/create-matter",
                        serializeState({
                            openStep: 1,
                            form1State: state.form1State,
                            form2State: state.form2State,
                            form3State: state.form3State,

                            resourceList: [
                                ...(state.resourceList || []),
                                normalized,
                            ],

                            subcontractorList:
                                state.subcontractorList || [],

                            editMode: state.editMode,
                            editData: state.editData,

                            fromAddResource: true,
                        })
                    );
                }}
            />
        </PageWrap>
    );
};

const mapStateToProps = (state) => ({
    userList: getUserList(state),
    allDepartmentList: getAllDepartmentList(state),
});

export default connect(mapStateToProps, null)(AddResourcesPage);

// ─── Styled ───────────────────────────────────────────────────────────────────

const ScheduleWrap = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: start;
  gap: 2px;

  .schedule { padding: 0 0.2rem; margin-left: 0; }

  .day {
    width: 90px; height: 38px; border-radius: 5px;
    padding: 6px 12px; text-align: center; font-weight: 600;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &.available { background: ${AvailableColor}; }
    &.booked    { background: ${BookedColor}; }
  }
`;

const PageWrap = styled.div`
  padding: 16px;
  background: #fff;
  min-height: 100vh;

  .legend {
    display: flex; gap: 0.5rem;
    .legend-box {
      width: 20px; height: 20px; border-radius: 5px;
      &.available { background: ${AvailableColor}; }
      &.booked    { background: ${BookedColor}; }
    }
  }
`;